import React, { useState } from "react";
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
  Collapse,
  Link as MuiLink,
  Tooltip,
} from "@mui/material";
import { Link, useHistory } from 'react-router-dom';
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";

import { useDispatch, useStore } from "react-redux";
import {
  fetchSavedScenario,
} from 'store/reducers/inputReducer';
import { cloneExampleAsTemplate } from 'components/domain/examples/useExampleAsTemplate';
import { getModelTypeMeta, flattenSummary } from 'store/functions/exampleCatalog.js';
import { cardTagsOf } from 'components/domain/use-cases/exampleFilters';
import fallbackBanner from "assets/img/theme/img-1-1000x600.jpg";


function CardUseCase(props) {
  const { example } = props;
  const history = useHistory();
  const dispatch = useDispatch();
  const store = useStore();
  const [constraintsExpanded, setConstraintsExpanded] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);

  const modelMeta = getModelTypeMeta(example.model_type);
  // Skip the "Model Type" stat — the model chip already conveys it.
  const summaryStats = flattenSummary(example.summary).filter(
    (s) => s.label.toLowerCase() !== "model type"
  );
  const constraints = example.constraints || [];
  // Show up to 3 constraints inline and collapse the rest behind a "+ N more"
  // toggle — but if only one would be hidden, the toggle takes the same space as
  // the item itself, so just show it.
  const CONSTRAINT_LIMIT = 3;
  const collapseConstraints = constraints.length > CONSTRAINT_LIMIT + 1;
  const visibleConstraints = collapseConstraints ? CONSTRAINT_LIMIT : constraints.length;
  // Prefer the clean constraint set so the chips line up 1:1 with the gallery's
  // Constraints facet; legacy rows without it fall back to `tags`.
  const tags = cardTagsOf(example);

  const renderConstraint = (constraint) => (
    <ListItem key={constraint} sx={{ p: 0, mb: 0.25 }}>
      <Box sx={{ width: 5, height: 5, borderRadius: "50%", backgroundColor: "primary.main", mr: 1, flexShrink: 0 }} />
      <ListItemText
        primary={constraint}
        primaryTypographyProps={{ variant: "body2", sx: { color: "text.secondary", fontSize: "0.75rem" } }}
      />
    </ListItem>
  );

  const useAsTemplateClick = async (event) => {
    event.stopPropagation();
    if (loadingTemplate) return;
    setLoadingTemplate(true);
    try {
      const resultAction = await dispatch(
        fetchSavedScenario({
          scenarioId: example.instance_id,
          exampleSlug: example.slug,
          modelMode: true,
        })
      );
      if (fetchSavedScenario.fulfilled.match(resultAction)) {
        cloneExampleAsTemplate({
          example,
          dispatch,
          getState: store.getState,
          history,
          ctaId: "gallery_card_use_template",
          // The click happens on the gallery, not on the example's own page.
          page: "use-cases",
        });
      }
    } finally {
      setLoadingTemplate(false);
    }
  };

  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: 3,
        backgroundColor: "#FFFFFF",
        border: "1px solid",
        borderColor: "divider",
        borderTop: "4px solid",
        borderTopColor: "primary.main",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.02)",
        transition: "all 0.3s ease",
        display: "flex",
        flexDirection: "column",
        "&:hover": {
          borderColor: "primary.main",
          boxShadow: "0 10px 25px rgba(0, 146, 139, 0.1)",
          transform: "translateY(-6px)",
        },
      }}
    >
      <Box sx={{ position: "relative", pt: "50%", overflow: "hidden", backgroundColor: "primary.extraLight" }}>
        <Box
          component="img"
          alt={example.title}
          src={example.banner_image || fallbackBanner}
          loading="lazy"
          onError={(e) => {
            // A banner URL can 404 or fail (e.g. an example seeded before its
            // map image was generated). Fall back to the default rather than
            // leaving a broken-image icon on the card.
            if (e.currentTarget.src !== fallbackBanner) {
              e.currentTarget.src = fallbackBanner;
            }
          }}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: "auto",
            height: "auto",
            maxWidth: "90%",
            maxHeight: "90%",
            transform: "translate(-50%, -50%)",
            objectFit: "contain",
            transition: "transform 0.5s ease",
            "&:hover": {
              transform: "translate(-50%, -50%) scale(1.05)",
            },
          }}
        />
      </Box>

      <CardContent sx={{ px: 2, pt: 1, pb: 2, flexGrow: 1, display: "flex", flexDirection: "column" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.5, gap: 1 }}>
          <Typography sx={{ fontWeight: 700, color: "dark.main", fontSize: "1.05rem", lineHeight: 1.2 }}>
            {example.title || example.scenario_name}
          </Typography>
          <Chip
            label={modelMeta.verbose}
            size="small"
            sx={{
              height: "20px",
              fontSize: "0.65rem",
              fontWeight: 700,
              textTransform: "uppercase",
              backgroundColor: "rgba(0, 146, 139, 0.12)",
              // extraDark, not main: small uppercase text on the tint measures
              // 3.3:1. Same hue, clears AA.
              color: "primary.extraDark",
              borderRadius: "4px",
              flexShrink: 0
            }}
          />
        </Box>

        {summaryStats.length > 0 && (
          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem", mb: 1 }}>
            {summaryStats.map((s) => `${s.value} ${s.label.toLowerCase()}`).join(" · ")}
          </Typography>
        )}

        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            lineHeight: 1.5,
            mb: 2,
            minHeight: "3em",
            fontSize: "0.8125rem"
          }}
        >
          {example.brief_description}
        </Typography>

        {constraints.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "primary.extraDark", mb: 0.5, textTransform: "uppercase", fontSize: "0.7rem", letterSpacing: 1 }}>
              Key Constraints
            </Typography>
            {/* The Collapse and the show-more control used to sit inside this
                <List>, which renders a <ul> — so the list held a div and a span
                as direct children and stopped being a list to a screen reader.
                They are siblings now; the visible items keep real list
                semantics, and the control that governs the list is not an item
                of it. */}
            <List sx={{ p: 0 }}>
              {constraints.slice(0, visibleConstraints).map(renderConstraint)}
            </List>
            {collapseConstraints && (
              <>
                <Collapse in={constraintsExpanded}>
                  <List sx={{ p: 0 }}>
                    {constraints.slice(visibleConstraints).map(renderConstraint)}
                  </List>
                </Collapse>
                {/* Was a Typography with an onClick: no keyboard focus, no role,
                    no state — unreachable unless you could use a mouse. */}
                <MuiLink
                  component="button"
                  type="button"
                  variant="caption"
                  onClick={() => setConstraintsExpanded(!constraintsExpanded)}
                  aria-expanded={constraintsExpanded}
                  sx={{
                    color: "primary.extraDark",
                    fontStyle: "italic",
                    ml: 3,
                    p: 0,
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {constraintsExpanded ? "show less" : `+ ${constraints.length - visibleConstraints} more`}
                </MuiLink>
              </>
            )}
          </Box>
        )}

        <Box sx={{ mt: "auto", display: "flex", flexWrap: "wrap", gap: 1 }}>
          {tags.map((label) => {
            const isFiltered = (props.selectedTags || []).indexOf(label) > -1;
            return (
              <Chip
                key={label}
                label={label}
                size="small"
                onClick={() => props.onToggleTag && props.onToggleTag(label)}
                onDelete={isFiltered && props.onToggleTag ? () => props.onToggleTag(label) : undefined}
                sx={{
                  borderRadius: "6px",
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  backgroundColor: isFiltered ? "primary.main" : "primary.extraLight",
                  color: isFiltered ? "#fff" : "primary.extraDark",
                  "&:hover": {
                    backgroundColor: isFiltered ? "primary.extraDark" : "primary.light",
                  },
                  "& .MuiChip-deleteIcon": {
                    color: "#fff",
                    "&:hover": { color: "grey.200" },
                  },
                }}
              />
            );
          })}
        </Box>
      </CardContent>

      <Divider />

      <Box sx={{ p: 1.5, display: "flex", gap: 1.5 }}>
        <Button
          component={Link}
          to={"/examples/" + example.slug}
          variant="contained"
          size="small"
          fullWidth
          endIcon={<ArrowForwardIcon sx={{ fontSize: "12px !important" }} />}
          sx={{
            backgroundColor: "primary.main",
            fontWeight: 700,
            fontSize: "0.75rem",
            py: 0.75,
            boxShadow: "none",
            transition: "all 0.2s",
            "&:hover": { backgroundColor: "primary.main", boxShadow: "0 4px 12px rgba(0, 146, 139, 0.35)", transform: "translateY(-1px)" },
          }}
        >
          Explore Model
        </Button>
        <Tooltip
          title="Keeps this vehicle fleet, depots and settings, and clears sample stops for your data."
          arrow
        >
          <span style={{ width: "100%" }}>
            <Button
              onClick={useAsTemplateClick}
              disabled={loadingTemplate}
              variant="outlined"
              size="small"
              fullWidth
              startIcon={<ContentPasteIcon sx={{ fontSize: "12px !important" }} />}
              sx={{
                borderColor: "primary.main",
                color: "primary.extraDark",
                fontWeight: 700,
                fontSize: "0.75rem",
                py: 0.75,
                transition: "all 0.2s",
                "&:hover": { borderColor: "primary.main", backgroundColor: "rgba(0, 146, 139, 0.08)", transform: "translateY(-1px)" },
              }}
            >
              Use as template
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Card>
  );
}

CardUseCase.propTypes = {
  example: PropTypes.shape({
    slug: PropTypes.string,
    instance_id: PropTypes.string,
    title: PropTypes.string,
    scenario_name: PropTypes.string,
    banner_image: PropTypes.string,
    model_type: PropTypes.string,
    brief_description: PropTypes.string,
    constraints: PropTypes.arrayOf(PropTypes.string),
    tags: PropTypes.arrayOf(PropTypes.string),
    summary: PropTypes.array,
  }).isRequired,
  // Constraint chips double as filters: clicking one toggles that value in the
  // gallery's Constraints facet.
  selectedTags: PropTypes.arrayOf(PropTypes.string),
  onToggleTag: PropTypes.func,
};

export default CardUseCase;
